import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import { KiroMascot, type KiroAsset } from './KiroMascot';

/** Mapeia o tipo de toast → pose do Kiro (cai no ícone SVG se faltar PNG). */
const KIRO_FOR_KIND: Record<'success' | 'error' | 'info', KiroAsset> = {
  success: 'sucesso',
  error: 'erro',
  info: 'avatar',
};

/**
 * Toast minimo (sem dep externa). Mostra 1 toast por vez.
 *
 * API: `const toast = useToast(); toast.success('Criado'); toast.error('...');`
 * Implementacao: cada toast fica 3.5s, com icone SVG + animacao de
 * entrada slide-in. Tom baseado no tipo (success/error/info).
 * Posicao: bottom-right em desktop, top-center em mobile (mais natural
 * por causa do teclado virtual subindo).
 *
 * Se precisar de multiplos toasts empilhados, trocar por
 * react-hot-toast em V1.
 */

type Kind = 'success' | 'error' | 'info';
interface ToastState {
  id: number;
  kind: Kind;
  message: string;
}

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

const ICONS: Record<Kind, ReactNode> = {
  success: (
    <svg
      className="w-5 h-5 flex-shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 10-1.4 1.4l2 2a1 1 0 001.4 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg
      className="w-5 h-5 flex-shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.7 7.3a1 1 0 00-1.4 1.4L8.6 10l-1.3 1.3a1 1 0 101.4 1.4L10 11.4l1.3 1.3a1 1 0 001.4-1.4L11.4 10l1.3-1.3a1 1 0 00-1.4-1.4L10 8.6 8.7 7.3z"
        clipRule="evenodd"
      />
    </svg>
  ),
  info: (
    <svg
      className="w-5 h-5 flex-shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const push = useCallback((kind: Kind, message: string) => {
    setToast({ id: Date.now(), kind, message });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const api: ToastApi = {
    success: (msg) => push('success', msg),
    error: (msg) => push('error', msg),
    info: (msg) => push('info', msg),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          data-testid={`toast-${toast.kind}`}
          className={clsx(
            'fixed z-50 px-4 py-3 rounded-xl shadow-pop border text-sm font-medium flex items-start gap-2.5 max-w-sm',
            'inset-x-4 top-4 sm:inset-x-auto sm:top-auto sm:bottom-6 sm:right-6',
            'animate-[toastIn_0.25s_cubic-bezier(0.16,1,0.3,1)]',
            toast.kind === 'success' && 'bg-emerald-50 border-emerald-200 text-emerald-900',
            toast.kind === 'error' && 'bg-red-50 border-red-200 text-red-900',
            toast.kind === 'info' && 'bg-brand-50 border-brand-200 text-brand-900',
          )}
        >
          <span
            className={clsx(
              'flex-shrink-0',
              toast.kind === 'success' && 'text-emerald-600',
              toast.kind === 'error' && 'text-red-600',
              toast.kind === 'info' && 'text-brand-600',
            )}
          >
            <KiroMascot
              name={KIRO_FOR_KIND[toast.kind]}
              size={34}
              fallback={ICONS[toast.kind]}
            />
          </span>
          <span className="flex-1 leading-snug">{toast.message}</span>
        </div>
      )}
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast deve ser usado dentro de ToastProvider');
  return ctx;
}
